import {
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  TextEllipsis,
  ISODate,
  DataObjectType,
  Detail,
  RelationsTableType,
  useGetDataObjectRelationsTable,
  useGetDataObjectRelationsDataConflicts,
  Spinner,
} from '@atlas-frontend/sdk';
import { RelationsTable } from './RelationsTable';
import { DataConflictsTable } from './DataConflictsTable';
import { buildConflictRows } from './utils';
import { Button, Collapse } from 'antd';
import * as Icon from 'react-feather';
import { FC, useMemo } from 'react';
import translations from './RelationsTablesModal.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { CollapseProps } from 'antd/lib';
import styled from 'styled-components';
import { colorGray } from '@atlas-frontend/design-tokens';

const { addTranslations, t } = i18nNsFactory('relations-tables-modal');
addTranslations(translations);

export interface RelationsTablesModalParams {
  dataObjectType: DataObjectType;
  groupOrDescriptionId: string | number | undefined;
  valueDate: ISODate;
  version: number | string | undefined;
  isGroup: boolean;
  rootNodeName: string;
}
export interface RelationsTablesModalProps {
  params: RelationsTablesModalParams;
  onClose: () => void;
}

export const RelationsTablesModal: FC<RelationsTablesModalProps> = (props) => {
  const { params, onClose } = props;
  const { dataObjectType, isGroup, rootNodeName } = params;

  const filters = useMemo(() => {
    const {
      groupOrDescriptionId,
      valueDate,
      version,
      dataObjectType: type,
    } = params;
    return { type, groupOrDescriptionId, valueDate, version };
  }, [params]);

  const { data: outgoingRelations, isFetching: isFetchingOutgoing } =
    useGetDataObjectRelationsTable(
      { ...filters, tableType: RelationsTableType.OUTGOING },
      { enabled: !!params.groupOrDescriptionId && !!params.valueDate },
    );

  const { data: incomingRelations, isFetching: isFetchingIncoming } =
    useGetDataObjectRelationsTable(
      { ...filters, tableType: RelationsTableType.INCOMING },
      { enabled: !!params.groupOrDescriptionId && !!params.valueDate },
    );

  const { data: internalRelations, isFetching: isFetchingInternal } =
    useGetDataObjectRelationsTable(
      { ...filters, tableType: RelationsTableType.INTERNAL },
      { enabled: !!params.groupOrDescriptionId && !!params.valueDate },
    );

  const { data: dataConflicts, isFetching: isFetchingDataConflicts } =
    useGetDataObjectRelationsDataConflicts(filters, {
      enabled: !!params.groupOrDescriptionId && !!params.valueDate,
    });

  const conflictRows = useMemo(
    () => (dataConflicts ? buildConflictRows(dataConflicts) : undefined),
    [dataConflicts],
  );

  const items: CollapseProps['items'] = useMemo(
    () => [
      ...(incomingRelations?.length
        ? [
            {
              key: 'incomingRelations',
              label: (
                <strong>
                  {t('heading.incomingRelations', {
                    count: incomingRelations.length,
                  })}
                </strong>
              ),
              children: (
                <RelationsTable
                  data={incomingRelations}
                  type={RelationsTableType.INCOMING}
                />
              ),
            },
          ]
        : []),
      ...(internalRelations?.length
        ? [
            {
              key: 'internalRelations',
              label: (
                <strong>
                  {t('heading.internalRelations', {
                    count: internalRelations.length,
                  })}
                </strong>
              ),
              children: (
                <RelationsTable
                  data={internalRelations}
                  type={RelationsTableType.INTERNAL}
                />
              ),
            },
          ]
        : []),
      ...(outgoingRelations?.length
        ? [
            {
              key: 'outgoingRelations',
              label: (
                <strong>
                  {t('heading.outgoingRelations', {
                    count: outgoingRelations.length,
                  })}
                </strong>
              ),
              children: (
                <RelationsTable
                  data={outgoingRelations}
                  type={RelationsTableType.OUTGOING}
                />
              ),
            },
          ]
        : []),
      ...(conflictRows?.length
        ? [
            {
              key: 'dataConflicts',
              label: (
                <strong>
                  {t('heading.dataConflicts', { count: conflictRows.length })}
                </strong>
              ),
              children: <DataConflictsTable data={conflictRows} />,
            },
          ]
        : []),
    ],
    [outgoingRelations, incomingRelations, internalRelations, conflictRows],
  );

  const isFetchingData = useMemo(
    () =>
      isFetchingOutgoing ||
      isFetchingIncoming ||
      isFetchingInternal ||
      isFetchingDataConflicts,
    [
      isFetchingOutgoing,
      isFetchingIncoming,
      isFetchingInternal,
      isFetchingDataConflicts,
    ],
  );

  const totalCount = useMemo(
    () =>
      (outgoingRelations?.length ?? 0) +
      (incomingRelations?.length ?? 0) +
      (internalRelations?.length ?? 0) +
      (conflictRows?.length ?? 0),
    [outgoingRelations, incomingRelations, internalRelations, conflictRows],
  );

  const rootNodeLabel = useMemo(() => {
    let tKey = 'inputDescription';
    if (dataObjectType === DataObjectType.IN && isGroup) tKey = 'inputGroup';
    if (dataObjectType === DataObjectType.OUT) {
      if (isGroup) tKey = 'outputGroup';
      else tKey = 'outputDescription';
    }
    return t(`label.${tKey}`);
  }, [dataObjectType, isGroup]);

  return (
    <Modal
      onCancel={onClose}
      width="100%"
      footer={
        <ModalFooter>
          <Button
            id="close-relations-tables-modal"
            type="primary"
            onClick={onClose}
          >
            {t('button.close')}
          </Button>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <ModalTitle>
          <TextEllipsis>{t('title', { count: totalCount })}</TextEllipsis>
        </ModalTitle>
      </ModalHeader>
      <Padding scrollable>
        <Detail label={rootNodeLabel}>{rootNodeName}</Detail>
        <Padding top>
          {isFetchingData ? (
            <Spinner message={t('spinner')} />
          ) : (
            <StyledCollapse
              ghost
              items={items}
              expandIcon={({ isActive }) =>
                isActive ? (
                  <Icon.ChevronDown size={28} color={colorGray[7]} />
                ) : (
                  <Icon.ChevronRight size={28} color={colorGray[7]} />
                )
              }
            />
          )}
        </Padding>
      </Padding>
    </Modal>
  );
};

const StyledCollapse = styled(Collapse)`
  .ant-collapse-item {
    padding: 0px 8px;
    border-bottom: 1px solid ${colorGray[1]};
  }
`;
